import { useEffect, useRef } from "react";
import Image, { type StaticImageData } from "./Image";

// Pin-art hover for the decorative ASCII illustrations, modelled on a pin art
// board: the art is treated as a grid of small square pins, and the characters
// under the cursor pop out of their slots toward the viewer, light up and cast
// a small shadow, then settle back once the cursor moves on. Any dark backing
// panel in the art stays put, like the board itself. Nothing runs until the art
// is hovered, and the plain <img> is shown again as soon as every pin is back
// at rest. Visitors who prefer reduced motion get the light-up without movement.
//
// All drawing happens in whole device pixels, so raised characters stay crisp
// and pins rise in one-pixel steps, like pins set at different heights.

const CELL = 6; // pin size, CSS px
const RADIUS = 44; // reach of the cursor, CSS px
const MAX_LIFT = 6; // how far a fully raised head moves, CSS px
const LIFT_X = -0.35; // heads move up and slightly left, lit from the top left
const LIFT_Y = -0.94;
const SHADOW = 3; // shadow offset down and right of a fully raised head, CSS px
const SHADOW_ALPHA = 0.8;
const RISE = 0.3; // easing per 60fps frame toward a higher target
const SETTLE = 0.08; // easing per 60fps frame back down
const REST = 0.004; // below this a pin counts as flat
const PAD = MAX_LIFT + SHADOW; // canvas overhang so heads near the edge aren't clipped, CSS px

type Pins = {
  dpr: number;
  pw: number; // image size in device px
  ph: number;
  cell: number; // pin size in device px
  pad: number; // canvas overhang in device px
  cols: number;
  rows: number;
  base: HTMLCanvasElement; // the art as-is
  ink: HTMLCanvasElement; // just the characters, without any dark backing panel
  bright: HTMLCanvasElement; // characters lit up, for raised heads
  shadow: HTMLCanvasElement; // character silhouettes, for shadows on the board
  index: Int32Array; // grid cell -> pin id, or -1 where the cell has no characters
  gx: Uint16Array;
  gy: Uint16Array;
  height: Float32Array;
  seen: Uint32Array;
};

function layer(src: CanvasImageSource, w: number, h: number) {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  c.getContext("2d")!.drawImage(src, 0, 0, w, h);
  return c;
}

function buildPins(img: HTMLImageElement): Pins {
  const rect = img.getBoundingClientRect();
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const pw = Math.round(rect.width * dpr);
  const ph = Math.round(rect.height * dpr);
  const cell = Math.max(2, Math.round(CELL * dpr));
  const base = layer(img, pw, ph);

  // Keep only pixels bright enough to be part of a character. Pixels are read
  // from a throwaway canvas so the layers drawn every frame stay on the GPU.
  const pixels = layer(img, pw, ph).getContext("2d")!.getImageData(0, 0, pw, ph);
  const d = pixels.data;
  for (let i = 0; i < d.length; i += 4) {
    const v = Math.max(d[i], d[i + 1], d[i + 2]) / 255;
    d[i + 3] = Math.round(d[i + 3] * Math.min(1, Math.max(0, (v - 0.18) / 0.17)));
  }
  const ink = document.createElement("canvas");
  ink.width = pw;
  ink.height = ph;
  ink.getContext("2d")!.putImageData(pixels, 0, 0);

  const bright = layer(ink, pw, ph);
  const brightCtx = bright.getContext("2d")!;
  brightCtx.globalCompositeOperation = "lighter";
  brightCtx.globalAlpha = 0.7;
  brightCtx.drawImage(ink, 0, 0);
  const shadow = layer(ink, pw, ph);
  const shadowCtx = shadow.getContext("2d")!;
  shadowCtx.globalCompositeOperation = "source-atop";
  shadowCtx.fillStyle = "rgb(2, 4, 10)";
  shadowCtx.fillRect(0, 0, pw, ph);

  // Only cells containing part of a character become pins.
  const cols = Math.ceil(pw / cell);
  const rows = Math.ceil(ph / cell);
  const index = new Int32Array(cols * rows).fill(-1);
  const gx: number[] = [];
  const gy: number[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      let found = false;
      for (let y = r * cell; y < Math.min(ph, (r + 1) * cell) && !found; y++) {
        for (let x = c * cell; x < Math.min(pw, (c + 1) * cell); x++) {
          if (d[(y * pw + x) * 4 + 3] > 24) {
            found = true;
            break;
          }
        }
      }
      if (found) {
        index[r * cols + c] = gx.length;
        gx.push(c);
        gy.push(r);
      }
    }
  }

  return {
    dpr, pw, ph, cell, cols, rows, base, ink, bright, shadow, index,
    pad: Math.ceil(PAD * dpr),
    gx: Uint16Array.from(gx),
    gy: Uint16Array.from(gy),
    height: new Float32Array(gx.length),
    seen: new Uint32Array(gx.length),
  };
}

interface AsciiArtProps {
  src: StaticImageData | string;
  width?: number;
  height?: number;
}

function AsciiArt({ src, width, height }: AsciiArtProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const img = wrap?.querySelector("img");
    const ctx = canvas?.getContext("2d");
    if (!wrap || !canvas || !img || !ctx) return;

    let pins: Pins | null = null;
    let pointer: { x: number; y: number } | null = null; // device px, relative to the image
    let active: number[] = [];
    let lift = MAX_LIFT;
    let frameId = 0;
    let last = 0;
    let stamp = 0;

    const draw = (p: Pins) => {
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(p.base, p.pad, p.pad);
      if (active.length === 0) return;

      // Copies one pin's cell from `src`, moved by whole device pixels.
      const blit = (src: HTMLCanvasElement, id: number, dx: number, dy: number) => {
        const x = p.gx[id] * p.cell;
        const y = p.gy[id] * p.cell;
        const cw = Math.min(p.cell, p.pw - x);
        const ch = Math.min(p.cell, p.ph - y);
        ctx.drawImage(src, x, y, cw, ch, p.pad + x + dx, p.pad + y + dy, cw, ch);
      };

      // 1. Lift each raised character out of its slot, leaving the board behind.
      ctx.globalCompositeOperation = "destination-out";
      for (const id of active) {
        ctx.globalAlpha = Math.min(1, p.height[id] * 3);
        blit(p.ink, id, 0, 0);
      }
      ctx.globalCompositeOperation = "source-over";

      // 2. Shadows cast on the board.
      if (lift) {
        for (const id of active) {
          const t = p.height[id];
          const s = Math.round(SHADOW * p.dpr * t);
          ctx.globalAlpha = SHADOW_ALPHA * t;
          blit(p.shadow, id, s, s);
        }
      }

      // 3. The raised heads, tallest last so they sit in front of their neighbours.
      active.sort((a, b) => p.height[a] - p.height[b]);
      for (const id of active) {
        const t = p.height[id];
        const dx = Math.round(LIFT_X * lift * p.dpr * t);
        const dy = Math.round(LIFT_Y * lift * p.dpr * t);
        ctx.globalAlpha = 1;
        blit(p.ink, id, dx, dy);
        ctx.globalAlpha = t;
        blit(p.bright, id, dx, dy);
      }
      ctx.globalAlpha = 1;
    };

    const rest = () => {
      frameId = 0;
      canvas.style.display = "";
      img.style.opacity = "";
    };

    const frame = (now: number) => {
      const p = pins!;
      const dt = last ? Math.min(4, (now - last) / (1000 / 60)) : 1;
      last = now;
      const kRise = 1 - Math.pow(1 - RISE, dt);
      const kSettle = 1 - Math.pow(1 - SETTLE, dt);
      const radius = RADIUS * p.dpr;
      const next: number[] = [];
      let moving = false;
      stamp++;

      const update = (id: number) => {
        if (p.seen[id] === stamp) return;
        p.seen[id] = stamp;
        let target = 0;
        if (pointer) {
          const d = Math.hypot((p.gx[id] + 0.5) * p.cell - pointer.x, (p.gy[id] + 0.5) * p.cell - pointer.y) / radius;
          if (d < 1) target = (1 - d * d) ** 2;
        }
        const cur = p.height[id];
        let h = cur + (target - cur) * (target > cur ? kRise : kSettle);
        if (h < REST && target === 0) h = 0;
        p.height[id] = h;
        if (Math.abs(target - h) > REST) moving = true;
        if (h > 0) next.push(id);
      };

      active.forEach(update);
      if (pointer) {
        const c0 = Math.max(0, Math.floor((pointer.x - radius) / p.cell));
        const c1 = Math.min(p.cols - 1, Math.floor((pointer.x + radius) / p.cell));
        const r0 = Math.max(0, Math.floor((pointer.y - radius) / p.cell));
        const r1 = Math.min(p.rows - 1, Math.floor((pointer.y + radius) / p.cell));
        for (let r = r0; r <= r1; r++) {
          for (let c = c0; c <= c1; c++) {
            const id = p.index[r * p.cols + c];
            if (id >= 0) update(id);
          }
        }
      }
      active = next;
      draw(p);

      if (!pointer && active.length === 0) rest();
      else if (moving) frameId = requestAnimationFrame(frame);
      else frameId = 0; // held still under a resting cursor; the next move wakes it
    };

    const wake = () => {
      if (!frameId && pins) {
        last = 0;
        frameId = requestAnimationFrame(frame);
      }
    };

    const onMove = (e: PointerEvent) => {
      if (!pins || e.pointerType === "touch") return;
      const rect = img.getBoundingClientRect();
      pointer = { x: (e.clientX - rect.left) * pins.dpr, y: (e.clientY - rect.top) * pins.dpr };
      wake();
    };

    // Builds the pins on first use, and again if the art's size or the screen's
    // pixel density has changed since. Returns null while the art isn't shown.
    const ensurePins = () => {
      const rect = img.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      if (rect.width < 1 || rect.height < 1 || !img.complete || !img.naturalWidth) return null;
      if (!pins || pins.dpr !== dpr || pins.pw !== Math.round(rect.width * dpr) || pins.ph !== Math.round(rect.height * dpr)) {
        pins = buildPins(img);
        active = [];
        canvas.width = pins.pw + 2 * pins.pad;
        canvas.height = pins.ph + 2 * pins.pad;
        canvas.style.width = `${canvas.width / pins.dpr}px`;
        canvas.style.height = `${canvas.height / pins.dpr}px`;
        canvas.style.margin = `${-pins.pad / pins.dpr}px`;
      }
      return pins;
    };

    const onEnter = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      const p = ensurePins();
      if (!p) return;
      lift = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : MAX_LIFT;
      if (canvas.style.display !== "block") {
        // Swap the <img> for the canvas only once the canvas holds the same picture.
        canvas.style.display = "block";
        draw(p);
        img.style.opacity = "0";
      }
      onMove(e);
    };

    const onLeave = () => {
      pointer = null;
      wake();
    };

    // Do the one-off pixel work while the browser is idle, shortly before the
    // art scrolls into view, so the first hover doesn't stall.
    const whenIdle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 200));
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        whenIdle(() => {
          if (canvas.style.display !== "block") ensurePins();
        });
      },
      { rootMargin: "300px" },
    );
    const observe = () => observer.observe(wrap);
    if (img.complete) observe();
    else img.addEventListener("load", observe, { once: true });

    wrap.addEventListener("pointerenter", onEnter);
    wrap.addEventListener("pointermove", onMove);
    wrap.addEventListener("pointerleave", onLeave);
    return () => {
      observer.disconnect();
      img.removeEventListener("load", observe);
      wrap.removeEventListener("pointerenter", onEnter);
      wrap.removeEventListener("pointermove", onMove);
      wrap.removeEventListener("pointerleave", onLeave);
      cancelAnimationFrame(frameId);
      rest();
    };
  }, []);

  // A one-cell grid stacks the canvas on the <img> without positioning either,
  // so the art keeps its place in the page's paint order. The canvas's negative
  // margins (set once its size is known) let raised heads overhang the image
  // edges without affecting layout.
  return (
    <div ref={wrapRef} className="grid">
      <Image src={src} width={width} height={height} alt='' className="col-start-1 row-start-1" />
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="col-start-1 row-start-1 hidden pointer-events-none"
      />
    </div>
  );
}

export default AsciiArt;
